import { yupResolver } from "@hookform/resolvers/yup";
import { errorHandler } from "_services/errorHandler";
import {
  useDriverServiceV1GetDriverDetailQuery,
  useDriverServiceV1GetDriverDocumentsQuery,
  useDriverServiceV1UpdateDriverMutation,
  useDriverServiceV1UpdateDriverDraftMutation,
  DriverServiceV1UpdateDriverBody,
  DriverServiceV1UpdateDriverDraftBody,
  TransportResourceGetDriverDetailRes,
} from "_services/modules/transport-resource/driver";

import {
  DriverFormValues,
  ExtendedDriverDetailRes,
  ExtendedDriverDocument,
  ExtendedUpdateDriverBody as ExtendedUpdateBody,
  ExtendedUpdateDriverDraftBody as ExtendedUpdateDraftBody,
} from "_interfaces/driver.interfaces";
import { validateUploadFile, translateFileValidationError, DOCUMENT_UPLOAD_OPTIONS } from "_helper/fileValidation";

const normalizeDriverFormStatus = (status?: string): DriverFormValues["status"] => {
  if (!status) return "active";
  const s = status.toLowerCase();
  if (s === "inactive") return "inactive";
  if (s === "active" || s === "available") return "active";
  return "active";
};

const mapStatusToBackend = (status?: string): string => {
  if (!status) return "Available";
  const statusLower = status.toLowerCase();
  if (statusLower === "inactive") return "Inactive";
  if (statusLower === "active" || statusLower === "available") return "Available";
  return "Available";
};

const normalizeRelation = (relation?: string): string => {
  if (!relation) return "";
  const r = relation.toLowerCase();
  if (r === "mother") return "Mother";
  if (r === "father") return "Father";
  if (r === "spouse") return "Spouse";
  if (r === "son") return "Son";
  if (r === "daughter") return "Daughter";
  if (r === "friend") return "Friend";
  return relation;
};

const normalizeGender = (gender?: string): string => {
  if (!gender) return "";
  const g = gender.toLowerCase();
  if (g === "male" || g === "men") return "Men";
  if (g === "female" || g === "women") return "Women";
  return gender;
};

const normalizeMaritalStatus = (status?: string): string => {
  if (!status) return "";
  const s = status.toLowerCase();
  if (s === "single") return "Single";
  if (s === "married") return "Married";
  if (s === "divorced") return "Divorced";
  return status;
};

import { useDriverDocumentServiceV1UploadDriverDocumentMutation } from "_services/modules/transport-resource/driver-document";
import {
  DriverDocumentType,
  DriverDocumentErrors,
} from "_interfaces/driver.interfaces";
import { useEffect, useState, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { useAppTranslation } from "locale/useAppTranslation";
import { useVendorSelectOptions } from "modules/master-data/vendor/list/useVendorList";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { driverListPageRouteName } from "../constants/routes";
import { REQUIRED_DRIVER_DOCUMENTS } from "../constants/options";

const formatPhoneNumber = (countryCode: string, number?: string): string | undefined => {
  if (!number) return undefined;
  let cleanNumber = number.replace(/\s+/g, "");
  
  if (cleanNumber.startsWith("+")) {
    cleanNumber = cleanNumber.slice(1);
  }
  
  const ccWithoutPlus = countryCode.replace("+", "");
  if (cleanNumber.startsWith(ccWithoutPlus)) {
    cleanNumber = cleanNumber.slice(ccWithoutPlus.length);
  }
  
  if (cleanNumber.startsWith("0")) {
    cleanNumber = cleanNumber.slice(1);
  }
  
  return `${countryCode}${cleanNumber}`;
};

const schema = yup
  .object({
    driverName: yup.string().required("Driver name is required"),
    ktpNumber: yup
      .string()
      .matches(/^\d{16}$/, "KTP number must be 16 digits")
      .required("KTP number is required"),
    phoneCountryCode: yup.string().required(),
    phoneNumber: yup.string().required("Phone number is required"),
    associatedVendor: yup.string().required("Associated vendor is required"),
    domicileAddress: yup.string().required("Domicile address is required"),
    citizenAddress: yup.string().required("Citizen address is required"),
    isSameAddress: yup.boolean().default(false),
    status: yup
      .mixed<"active" | "inactive">()
      .oneOf(["active", "inactive"])
      .required(),
    emergencyName: yup.string().required("Emergency contact name is required"),
    emergencyRelation: yup.string().required("Emergency relation is required"),
    emergencyPhoneCountryCode: yup.string().required(),
    emergencyPhoneNumber: yup
      .string()
      .required("Emergency contact phone is required"),
    bankName: yup.string().required("Bank name is required"),
    bankAccountNumber: yup.string().required("Bank account number is required"),
    bankAccountHolder: yup.string().required("Account holder name is required"),
    licenseType: yup.string().required("License type is required"),
    licenseNumber: yup.string().required("License number is required"),
    licenseExpiryDate: yup.string().required("License expiry date is required"),
    email: yup
      .string()
      .email("Please enter a valid email")
      .required("Email is required"),
    gender: yup.string().optional(),
    maritalStatus: yup.string().optional(),
    driverId: yup.string().optional(),
    joinDate: yup.string().optional(),
  })
  .required();
const useEditDriver = (id: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useAppTranslation("driver");
  const { vendorOptions, isLoadingVendorOptions, onSearchVendors } =
    useVendorSelectOptions(5);
  // ======================
  // API
  // ======================
  const { data: driverDetail, isLoading: isLoadingDetail } =
    useDriverServiceV1GetDriverDetailQuery(
      { id },
      { refetchOnMountOrArgChange: true }
    );
  const { data: driverDocuments, isLoading: isLoadingDocs } =
    useDriverServiceV1GetDriverDocumentsQuery(
      { id },
      { refetchOnMountOrArgChange: true }
    );
  const [updateDriver, { isLoading: isUpdating }] =
    useDriverServiceV1UpdateDriverMutation();
  const [updateDriverDraft, { isLoading: isUpdatingDraft }] =
    useDriverServiceV1UpdateDriverDraftMutation();
  const [uploadDriverDocument, { isLoading: isUploadingDriverPicture }] =
    useDriverDocumentServiceV1UploadDriverDocumentMutation();
  // ======================
  // STATE
  // ======================
  const isDraft = driverDetail?.status === "Draft";
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const form = useForm<DriverFormValues>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      driverName: "",
      ktpNumber: "",
      phoneCountryCode: "+62",
      phoneNumber: "",
      associatedVendor: "",
      domicileAddress: "",
      citizenAddress: "",
      isSameAddress: false,
      status: "active",
      emergencyName: "",
      emergencyRelation: "",
      emergencyPhoneCountryCode: "+62",
      emergencyPhoneNumber: "",
      bankName: "",
      bankAccountNumber: "",
      bankAccountHolder: "",
      licenseType: "",
      licenseNumber: "",
      licenseExpiryDate: "",
      gender: "",
      maritalStatus: "",
      email: "",
      driverId: "",
      joinDate: "",
    },
  });
  const [driverPictureFile, setDriverPictureFile] = useState<File>();
  const [driverPictureName, setDriverPictureName] = useState("");
  const [driverPictureUrl, setDriverPictureUrl] = useState("");
  const [documentFiles, setDocumentFiles] = useState<Record<string, string>>(
    {}
  );
  const [expiryDates, setExpiryDates] = useState<Record<string, string>>({});
  const handleExpiryDateChange = (type: string, date: string) => {
    setExpiryDates((prev) => ({ ...prev, [type]: date }));
  };
  const [documentErrors, setDocumentErrors] = useState<DriverDocumentErrors>({});

  const validateDocuments = useCallback((): boolean => {
    const errors: DriverDocumentErrors = {};
    const missingLabels: string[] = [];

    REQUIRED_DRIVER_DOCUMENTS.forEach((doc) => {
      const isUploaded = Boolean(documentFiles[doc.type]);
      if (!isUploaded) {
        const docName =
          doc.type === "domicile_letter"
            ? t("label-document-domicile") || "Domicile Letter"
            : doc.defaultLabel;
        errors[doc.type] =
          t("error-document-required", { doc: docName }) ||
          `${docName} document is required`;
        missingLabels.push(docName);
      }
    });

    setDocumentErrors(errors);

    if (missingLabels.length > 0) {
      toast.error(
        t("toast-missing-documents", { docs: missingLabels.join(", ") }) ||
          `${missingLabels.join(", ")} document(s) required`
      );
      return false;
    }

    return true;
  }, [documentFiles, t]);
  // ======================
  // EFFECT
  // ======================
  // Hydrate form only once per driver id so background refetches after
  // a save do not wipe in-progress user edits.
  const hydratedIdRef = useRef<string | null>(null);
  const hydratedDocsIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (driverDetail && hydratedIdRef.current !== id) {
      const detail = driverDetail as ExtendedDriverDetailRes;
      form.reset({
        driverName: driverDetail.full_name || "",
        ktpNumber: driverDetail.ktp_number || "",
        phoneCountryCode: "+62",
        phoneNumber: (driverDetail.phone_number || "").replace("+62", ""),
        associatedVendor: driverDetail.vendor_id || "",
        domicileAddress: driverDetail.domicile_address || "",
        citizenAddress: driverDetail.citizen_address || "",
        isSameAddress:
          driverDetail.domicile_address === driverDetail.citizen_address,
        status: normalizeDriverFormStatus(driverDetail.status),
        emergencyName: driverDetail.emergency_contact_name || "",
        emergencyRelation: normalizeRelation(driverDetail.emergency_contact_relation || ""),
        emergencyPhoneCountryCode: "+62",
        emergencyPhoneNumber: (driverDetail.emergency_contact_phone || "").replace("+62", ""),
        bankName: driverDetail.bank_name || "",
        bankAccountNumber: driverDetail.bank_account_number || "",
        bankAccountHolder: driverDetail.bank_account_holder || "",
        licenseType: driverDetail.license_type || "",
        licenseNumber: driverDetail.license_number || "",
        licenseExpiryDate: driverDetail.license_expiry_date || "",
        gender: normalizeGender(driverDetail.gender || ""),
        maritalStatus: normalizeMaritalStatus(driverDetail.marital_status || ""),
        email: driverDetail.email || "",
        driverId: detail.driver_code || detail.driver_id || "",
        joinDate: detail.join_date || "",
      });
      setDriverPictureUrl(detail.driver_picture_url || "");
      setDriverPictureFile(undefined);
      setDriverPictureName("");
      hydratedIdRef.current = id;
    }
  }, [driverDetail, form, id]);
  useEffect(() => {
    if (hydratedDocsIdRef.current === id) return;
    const detail = driverDetail as ExtendedDriverDetailRes;
    const docs = detail?.documents || driverDocuments?.documents;
    if (docs) {
      const files: Record<string, string> = {};
      const expDates: Record<string, string> = {};
      docs.forEach((doc: any) => {
        const docType = doc.document_type || doc.type;
        const docUrl = doc.document_url || doc.file_url;
        if (docType && docUrl) {
          const typeKey = docType.toLowerCase().replace(" ", "_");
          files[typeKey] = docUrl;
          expDates[typeKey] = doc.expiry_date || "";
        }
      });
      setDocumentFiles(files);
      setExpiryDates(expDates);
      hydratedDocsIdRef.current = id;
    }
  }, [driverDetail, driverDocuments, id]);
  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);
  // ======================
  // HANDLER
  // ======================
  const handleDriverPictureChange = (file?: File) => {
    if (!file) {
      setDriverPictureFile(undefined);
      setDriverPictureName("");
      setDriverPictureUrl("");
      return;
    }

    const validationError = validateUploadFile(file, {
      allowedExtensions: ["jpg", "jpeg", "png"],
      maxSizeMB: 5,
    });
    if (validationError) {
      const message = translateFileValidationError(t, validationError);
      toast.error(message);
      return;
    }

    setDriverPictureFile(file);
    setDriverPictureName(file.name);
    setDriverPictureUrl(URL.createObjectURL(file));
  };
  const uploadPictureIfNeeded = async (): Promise<string | undefined> => {
    if (!driverPictureFile) {
      return driverPictureUrl || undefined;
    }
    const formData = new FormData();
    formData.append("file", driverPictureFile);
    const uploadRes = await uploadDriverDocument({
      apiHttpBody: formData as any,
    }).unwrap();
    return uploadRes.file_url ?? "";
  };
  const handleDocumentFileChange = async (type: string, file?: File) => {
    setDocumentErrors((prev) => {
      const docType = type as DriverDocumentType;
      if (!prev[docType]) return prev;
      const next = { ...prev };
      delete next[docType];
      return next;
    });

    if (!file) {
      setDocumentFiles((current) => ({
        ...current,
        [type]: "",
      }));
      return;
    }

    const validationError = validateUploadFile(file, DOCUMENT_UPLOAD_OPTIONS);
    if (validationError) {
      const message = translateFileValidationError(t, validationError);
      setDocumentErrors((prev) => ({
        ...prev,
        [type as DriverDocumentType]: message,
      }));
      toast.error(message);
      return;
    }

    setDocumentErrors((prev) => {
      const docType = type as DriverDocumentType;
      if (!prev[docType]) return prev;
      const next = { ...prev };
      delete next[docType];
      return next;
    });
    setDocumentFiles((current) => ({
      ...current,
      [type]: file as any,
    }));
  };
  // ======================
  // SAVE
  // ======================
  const uploadDocumentsIfNeeded = async (): Promise<Record<string, string>> => {
    const next: Record<string, string> = { ...documentFiles } as Record<string, string>;
    await Promise.all(
      Object.entries(documentFiles).map(async ([type, value]) => {
        if (!((value as unknown) instanceof File)) return;
        const fileValue = value as unknown as File;
        const validationError = validateUploadFile(fileValue, DOCUMENT_UPLOAD_OPTIONS);
        if (validationError) {
          const message = translateFileValidationError(t, validationError);
          setDocumentErrors((prev) => ({
            ...prev,
            [type as DriverDocumentType]: message,
          }));
          throw new Error(message);
        }
        const formData = new FormData();
        formData.append("file", fileValue);
        const uploadRes = await uploadDriverDocument({
          apiHttpBody: formData as any,
        }).unwrap();
        next[type] = uploadRes.file_url ?? "";
      })
    );
    setDocumentFiles(next as any);
    return next;
  };
  const handleConfirmSave = async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error(t("toast-complete-required-fields"));
      setShowSaveModal(false);
      return;
    }
    const isDocsValid = validateDocuments();
    if (!isDocsValid) {
      setShowSaveModal(false);
      return;
    }
    let pictureUrl: string | undefined;
    try {
      pictureUrl = await uploadPictureIfNeeded();
    } catch (error) {
      errorHandler(error);
      return;
    }
    let uploadedFiles: Record<string, string>;
    try {
      uploadedFiles = await uploadDocumentsIfNeeded();
    } catch (error) {
      errorHandler(error);
      setShowSaveModal(false);
      return;
    }
    const values = form.getValues();
    const documents = Object.entries(uploadedFiles)
      .filter(([, fileUrl]) => Boolean(fileUrl))
      .map(([type, fileUrl]) => ({
        document_type: type === "domicile_letter" ? "Domicile Letter" : type.toUpperCase(),
        document_url: fileUrl as string,
        expiry_date: expiryDates[type] || undefined,
      }));
    try {
      const updateBody: ExtendedUpdateBody = {
        full_name: values.driverName,
        ktp_number: values.ktpNumber,
        phone_number: formatPhoneNumber(values.phoneCountryCode, values.phoneNumber) || "",
        email: values.email,
        vendor_id: values.associatedVendor,
        citizen_address: values.citizenAddress,
        domicile_address: values.domicileAddress,
        license_type: values.licenseType,
        license_number: values.licenseNumber,
        license_expiry_date: values.licenseExpiryDate,
        gender: values.gender || undefined,
        marital_status: values.maritalStatus || undefined,
        bank_name: values.bankName,
        bank_account_number: values.bankAccountNumber,
        bank_account_holder: values.bankAccountHolder,
        status: mapStatusToBackend(values.status),
        documents,
        driver_picture_url: pictureUrl || undefined,
        emergency_contact_name: values.emergencyName,
        emergency_contact_phone: formatPhoneNumber(values.emergencyPhoneCountryCode, values.emergencyPhoneNumber) || "",
        emergency_contact_relation: values.emergencyRelation,
        join_date: values.joinDate || undefined,
      };

      await updateDriver({
        id,
        driverServiceV1UpdateDriverBody: updateBody as unknown as DriverServiceV1UpdateDriverBody,
      }).unwrap();
      toast.success(t("toast-update-success"));
      if (location.state?.from === "draft") {
        navigate("/master-data/driver/draft");
      } else if (location.state?.from === "detail") {
        navigate(`/master-data/driver/${id}/detail`);
      } else {
        navigate(driverListPageRouteName);
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setShowSaveModal(false);
    }
  };
  // ======================
  // SAVE DRAFT
  // ======================
  const handleSaveDraft = async () => {
    let pictureUrl: string | undefined;
    try {
      pictureUrl = await uploadPictureIfNeeded();
    } catch (error) {
      errorHandler(error);
      return;
    }
    let uploadedFiles: Record<string, string>;
    try {
      uploadedFiles = await uploadDocumentsIfNeeded();
    } catch (error) {
      errorHandler(error);
      return;
    }
    const values = form.getValues();
    const documents = Object.entries(uploadedFiles)
      .filter(([, fileUrl]) => Boolean(fileUrl))
      .map(([type, fileUrl]) => ({
        document_type: type === "domicile_letter" ? "Domicile Letter" : type.toUpperCase(),
        document_url: fileUrl as string,
        expiry_date: expiryDates[type] || undefined,
      }));
    try {
      const updateDraftBody: ExtendedUpdateDraftBody = {
        full_name: values.driverName || undefined,
        ktp_number: values.ktpNumber || undefined,
        phone_number: formatPhoneNumber(values.phoneCountryCode, values.phoneNumber),
        email: values.email || undefined,
        vendor_id: values.associatedVendor || undefined,
        citizen_address: values.citizenAddress || undefined,
        domicile_address: values.domicileAddress || undefined,
        license_type: values.licenseType || undefined,
        license_number: values.licenseNumber || undefined,
        license_expiry_date: values.licenseExpiryDate || undefined,
        gender: values.gender || undefined,
        marital_status: values.maritalStatus || undefined,
        bank_name: values.bankName || undefined,
        bank_account_number: values.bankAccountNumber || undefined,
        bank_account_holder: values.bankAccountHolder || undefined,
        documents,
        driver_picture_url: pictureUrl || undefined,
        emergency_contact_name: values.emergencyName || undefined,
        emergency_contact_phone: formatPhoneNumber(values.emergencyPhoneCountryCode, values.emergencyPhoneNumber),
        emergency_contact_relation: values.emergencyRelation || undefined,
        driver_code: values.driverId || undefined,
        join_date: values.joinDate || undefined,
      };

      await updateDriverDraft({
        id,
        driverServiceV1UpdateDriverDraftBody: updateDraftBody as unknown as DriverServiceV1UpdateDriverDraftBody,
      }).unwrap();
      toast.success(t("toast-update-success"));
      if (location.state?.from === "draft") {
        navigate("/master-data/driver/draft");
      } else if (location.state?.from === "detail") {
        navigate(`/master-data/driver/${id}/detail`);
      } else {
        navigate(driverListPageRouteName);
      }
    } catch (error) {
      errorHandler(error);
    }
  };
  return {
    t,
    form,
    vendorOptions,
    isLoadingVendorOptions,
    isUpdating,
    isUpdatingDraft,
    isLoadingDetail: isLoadingDetail || isLoadingDocs,
    onSearchVendors,
    driverPictureUrl,
    driverPictureName,
    isUploadingDriverPicture,
    documentFiles,
    documentErrors,
    setDocumentErrors,
    validateDocuments,
    expiryDates,
    handleExpiryDateChange,
    handleDriverPictureChange,
    handleDocumentFileChange,
    handleConfirmSave,
    handleSaveDraft,
    isDraft,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
  };
};
export default useEditDriver;
